/**
 * Device Command Builder Page
 * Bygg och skicka kommandon till enheter
 */

import { useState, useEffect, useRef } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { deviceCommandsService, type DeviceCommand } from '@/services/deviceCommands/deviceCommandsService';
import { devicesService, type Device } from '@/services/devices/devicesService';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, PlusIcon, TrashIcon, PaperAirplaneIcon, ChevronDownIcon, CommandLineIcon } from '@heroicons/react/24/outline';

interface SelectedCommand {
 id: string;
 command: DeviceCommand;
 parameters: Record<string, any>;
}

const DeviceCommandBuilder = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [commands, setCommands] = useState<DeviceCommand[]>([]);
 const [filteredCommands, setFilteredCommands] = useState<DeviceCommand[]>([]);
 const [loading, setLoading] = useState(false);
 const [selectedCommands, setSelectedCommands] = useState<SelectedCommand[]>([]);
 const [selectedImei, setSelectedImei] = useState('');
 const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
 const [filterType, setFilterType] = useState<'ALL' | 'GET' | 'SET' | 'ACTION' | 'CONFIG'>('ALL');
 const [searchTerm, setSearchTerm] = useState('');
 const [devices, setDevices] = useState<Device[]>([]);
 const [deviceSearchTerm, setDeviceSearchTerm] = useState('');
 const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
 const deviceDropdownRef = useRef<HTMLDivElement>(null);

 useEffect(() => {
 loadCommands();
 loadDevices();
 }, []);

 useEffect(() => {
 // Stäng dropdown när man klickar utanför
 const handleClickOutside = (event: MouseEvent) => {
 if (deviceDropdownRef.current && !deviceDropdownRef.current.contains(event.target as Node)) {
 setShowDeviceDropdown(false);
 }
 };

 document.addEventListener('mousedown', handleClickOutside);
 return () => {
 document.removeEventListener('mousedown', handleClickOutside);
 };
 }, []);

 useEffect(() => {
 // Sök enheter när search term ändras
 if (deviceSearchTerm) {
 const timeoutId = setTimeout(() => {
 loadDevices(deviceSearchTerm);
 }, 300); // Debounce 300ms
 return () => clearTimeout(timeoutId);
 } else {
 loadDevices();
 }
 }, [deviceSearchTerm]);

 useEffect(() => {
 filterCommands();
 }, [commands, filterType, searchTerm]);

 const loadCommands = async () => {
 setLoading(true);
 try {
 const data = await deviceCommandsService.getAllCommands();
 setCommands(data);
 } catch (error: any) {
 toast.error(t('devices.loadCommandsError') + ': ' + error.message);
 } finally {
 setLoading(false);
 }
 };

 const loadDevices = async (search?: string) => {
 try {
 const data = await devicesService.getAllDevices(search);
 setDevices(data);
 } catch (error: any) {
 toast.error(t('devices.loadDevicesError') + ': ' + error.message);
 }
 };

 const handleDeviceSelect = (device: Device) => {
 setSelectedDevice(device);
 setSelectedImei(device.imei);
 setDeviceSearchTerm('');
 setShowDeviceDropdown(false);
 };

 const filterCommands = () => {
 let filtered = commands;

 if (filterType !== 'ALL') {
 filtered = filtered.filter(cmd => cmd.command_type === filterType);
 }

 if (searchTerm) {
 const term = searchTerm.toLowerCase();
 filtered = filtered.filter(
 cmd =>
 cmd.name.toLowerCase().includes(term) ||
 cmd.code.toLowerCase().includes(term) ||
 (cmd.description && cmd.description.toLowerCase().includes(term))
 );
 }

 setFilteredCommands(filtered);
 };

 const addCommand = (command: DeviceCommand) => {
 const newCommand: SelectedCommand = {
 id: `${command.code}-${Date.now()}`,
 command,
 parameters: {},
 };

 // Fyll i default-värden från example_payload om det finns
 if (command.example_payload) {
 newCommand.parameters = { ...command.example_payload };
 }

 setSelectedCommands([...selectedCommands, newCommand]);
 };

 const removeCommand = (id: string) => {
 setSelectedCommands(selectedCommands.filter(cmd => cmd.id !== id));
 };

 const updateParameter = (commandId: string, paramName: string, value: any) => {
 setSelectedCommands(
 selectedCommands.map(cmd => {
 if (cmd.id === commandId) {
 return {
 ...cmd,
 parameters: {
 ...cmd.parameters,
 [paramName]: value,
 },
 };
 }
 return cmd;
 })
 );
 };

 const renderParameterInput = (command: SelectedCommand, paramName: string, paramSchema: any) => {
 const paramType = paramSchema.type || 'string';
 const value = command.parameters[paramName] || paramSchema.default || '';

 switch (paramType) {
 case 'integer':
 return (
 <input
 type="number"
 value={value}
 onChange={e => updateParameter(command.id, paramName, parseInt(e.target.value) || 0)}
 min={paramSchema.min}
 max={paramSchema.max}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 placeholder={paramSchema.description || paramName}
 />
 );

 case 'boolean':
 return (
 <select
 value={value ? 'true' : 'false'}
 onChange={e => updateParameter(command.id, paramName, e.target.value === 'true')}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 >
 <option value="false">{t('deviceCommands.booleanNo')}</option>
 <option value="true">{t('deviceCommands.booleanYes')}</option>
 </select>
 );

 case 'string':
 default:
 if (paramSchema.enum) {
 return (
 <select
 value={value}
 onChange={e => updateParameter(command.id, paramName, e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 >
 <option value="">{t('deviceCommands.selectOption')}</option>
 {paramSchema.enum.map((option: string) => (
 <option key={option} value={option}>
 {option}
 </option>
 ))}
 </select>
 );
 }
 return (
 <input
 type="text"
 value={value}
 onChange={e => updateParameter(command.id, paramName, e.target.value)}
 maxLength={paramSchema.maxLength}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 placeholder={paramSchema.description || paramName}
 />
 );
 }
 };

 const validateCommand = (selectedCommand: SelectedCommand): boolean => {
 const schema = selectedCommand.command.parameters_schema;
 const required = schema.required || [];

 for (const param of required) {
 if (!selectedCommand.parameters[param] && selectedCommand.parameters[param] !== 0) {
 toast.error(`${t('deviceCommands.requiredParameter')}: "${param}" (${selectedCommand.command.name})`);
 return false;
 }
 }

 return true;
 };

 const sendCommands = async () => {
 if (!selectedImei.trim()) {
 toast.error(t('deviceCommands.enterImei'));
 return;
 }

 if (selectedCommands.length === 0) {
 toast.error(t('deviceCommands.selectAtLeastOne'));
 return;
 }

 // Validera alla kommandon
 for (const cmd of selectedCommands) {
 if (!validateCommand(cmd)) {
 return;
 }
 }

 try {
 setLoading(true);

 const commandsToSend = selectedCommands.map(cmd => ({
 command_code: cmd.command.code,
 parameters: cmd.parameters,
 }));

 await api.post(`/api/devices/${selectedImei}/commands`, {
 commands: commandsToSend,
 priority: 5,
 vehicle_id: selectedDevice?.vehicle_id,
 vehicle_name: selectedDevice?.vehicle_name,
 });

 toast.success(t('deviceCommands.commandsSent'));
 
 // Rensa valda kommandon
 setSelectedCommands([]);
 setSelectedImei('');
 } catch (error: any) {
 toast.error(t('devices.sendCommandsError') + ': ' + (error.response?.data?.error || error.message));
 } finally {
 setLoading(false);
 }
 };

 const getCommandTypeColor = (type: string) => {
 switch (type) {
 case 'GET':
 return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
 case 'SET':
 return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
 case 'ACTION':
 return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
 case 'CONFIG':
 return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
 default:
 return 'bg-bgBase text-textDefault';
 }
 };

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <div className="mb-6">
 <div className="flex items-center gap-3">
 <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <CommandLineIcon className="w-6 h-6 text-primary" />
 {t('deviceCommands.title')}
 </h1>
 </div>
 <p className="mt-2 text-sm text-textSecondary">
 {t('deviceCommands.description')}
 </p>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 {/* Vänster: Kommandokatalog */}
 <div className="bg-bgElevated rounded-lg shadow p-6">
 <h2 className="text-xl font-semibold text-textDefault mb-4">
 {t('deviceCommands.availableCommands')}
 </h2>

 {/* Filter */}
 <div className="mb-4 space-y-2">
 <input
 type="text"
 placeholder={t('deviceCommands.searchCommands')}
 value={searchTerm}
 onChange={e => setSearchTerm(e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 />
 <div className="flex gap-2">
 {(['ALL', 'GET', 'SET', 'ACTION', 'CONFIG'] as const).map(type => (
 <button
 key={type}
 onClick={() => setFilterType(type)}
 className={`px-3 py-1 text-sm rounded-md ${
 filterType === type
 ? 'bg-blue-600 text-white'
 : 'bg-bgBase text-textDefault hover:bg-bgBase'
 }`}
 >
 {type}
 </button>
 ))}
 </div>
 </div>

 {/* Kommandolista */}
 <div className="space-y-2 max-h-96 overflow-y-auto">
 {loading ? (
 <div className="text-center py-8 text-textSecondary">{t('deviceCommands.loading')}</div>
 ) : filteredCommands.length === 0 ? (
 <div className="text-center py-8 text-textSecondary">{t('deviceCommands.noCommands')}</div>
 ) : (
 filteredCommands.map(command => (
 <div
 key={command.id}
 className="border border-border rounded-lg p-4 hover:bg-bgBase transition-colors"
 >
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-1">
 <h3 className="font-semibold text-textDefault">
 {command.name}
 </h3>
 <span
 className={`px-2 py-1 text-xs rounded ${getCommandTypeColor(
 command.command_type
 )}`}
 >
 {command.command_type}
 </span>
 </div>
 <p className="text-sm text-textSecondary mb-2">
 {command.description || command.code}
 </p>
 <code className="text-xs text-textSecondary">
 {command.code}
 </code>
 </div>
 <button
 onClick={() => addCommand(command)}
 className="ml-4 p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded"
 title={t('deviceCommands.addCommand')}
 >
 <PlusIcon className="w-5 h-5" />
 </button>
 </div>
 </div>
 ))
 )}
 </div>
 </div>

 {/* Höger: Valda kommandon och formulär */}
 <div className="bg-bgElevated rounded-lg shadow p-6">
 <h2 className="text-xl font-semibold text-textDefault mb-4">
 {t('deviceCommands.selectedCommands')} ({selectedCommands.length})
 </h2>

 {/* IMEI Dropdown */}
 <div className="mb-4 relative" ref={deviceDropdownRef}>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {t('deviceCommands.deviceImei')} <span className="text-red-500">*</span>
 </label>
 <div className="relative">
 <input
 type="text"
 value={deviceSearchTerm || selectedImei || ''}
 onChange={e => {
 setDeviceSearchTerm(e.target.value);
 setShowDeviceDropdown(true);
 if (!e.target.value) {
 setSelectedImei('');
 setSelectedDevice(null);
 }
 }}
 onFocus={() => setShowDeviceDropdown(true)}
 placeholder={t('deviceCommands.searchDevicePlaceholder')}
 className="w-full px-3 py-2 pr-10 border border-border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-bgElevated text-textDefault"
 />
 <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textTertiary pointer-events-none" />
 
 {showDeviceDropdown && (
 <div className="absolute z-10 w-full mt-1 bg-bgElevated border border-border rounded-md shadow-lg max-h-60 overflow-auto">
 {devices.length === 0 ? (
 <div className="px-4 py-3 text-sm text-textSecondary">
 {deviceSearchTerm ? t('deviceCommands.noDevicesFound') : t('deviceCommands.loadingDevices')}
 </div>
 ) : (
 devices.map(device => (
 <button
 key={device.id}
 type="button"
 onClick={() => handleDeviceSelect(device)}
 className="w-full text-left px-4 py-2 hover:bg-bgBase focus:bg-bgBase focus:outline-none"
 >
 <div className="flex items-center justify-between">
 <div>
 <div className="font-medium text-textDefault">
 {device.imei}
 </div>
 {device.vehicle_name && (
 <div className="text-sm text-textSecondary">
 {device.vehicle_name}
 </div>
 )}
 </div>
 {device.vehicle_type && (
 <span className="text-xs px-2 py-1 bg-bgBase text-textDefault rounded">
 {device.vehicle_type}
 </span>
 )}
 </div>
 </button>
 ))
 )}
 </div>
 )}
 </div>
 {selectedDevice && selectedDevice.vehicle_name && (
 <div className="mt-1 text-sm text-textSecondary">
 {t('deviceCommands.vehicleLabel')}: {selectedDevice.vehicle_name}
 </div>
 )}
 </div>

 {/* Valda kommandon */}
 <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
 {selectedCommands.length === 0 ? (
 <div className="text-center py-8 text-textSecondary">
 {t('deviceCommands.noCommandsSelected')}
 </div>
 ) : (
 selectedCommands.map(selectedCommand => {
 const schema = selectedCommand.command.parameters_schema;
 const properties = schema.properties || {};
 const required = schema.required || [];

 return (
 <div
 key={selectedCommand.id}
 className="border border-border rounded-lg p-4"
 >
 <div className="flex items-start justify-between mb-3">
 <div>
 <h3 className="font-semibold text-textDefault">
 {selectedCommand.command.name}
 </h3>
 <code className="text-xs text-textSecondary">
 {selectedCommand.command.code}
 </code>
 </div>
 <button
 onClick={() => removeCommand(selectedCommand.id)}
 className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded"
 title={t('deviceCommands.removeCommand')}
 >
 <TrashIcon className="w-4 h-4" />
 </button>
 </div>

 {/* Parametrar */}
 {Object.keys(properties).length > 0 && (
 <div className="space-y-2">
 {Object.entries(properties).map(([paramName, paramSchema]: [string, any]) => (
 <div key={paramName}>
 <label className="block text-sm font-medium text-textDefault mb-1">
 {paramSchema.description || paramName}
 {required.includes(paramName) && (
 <span className="text-red-500 ml-1">*</span>
 )}
 </label>
 {renderParameterInput(selectedCommand, paramName, paramSchema)}
 </div>
 ))}
 </div>
 )}
 </div>
 );
 })
 )}
 </div>

 {/* Skicka knapp */}
 <button
 onClick={sendCommands}
 disabled={loading || selectedCommands.length === 0 || !selectedImei.trim()}
 className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >
 <PaperAirplaneIcon className="w-5 h-5" />
 {loading ? t('deviceCommands.sending') : t('deviceCommands.sendCommands')}
 </button>
 </div>
 </div>
 </div>
 );
};

export default DeviceCommandBuilder;

